import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  LayoutDashboard,
  Ticket as TicketIcon,
  BookOpen,
  ScrollText,
  Laptop,
  FolderKanban,
  BarChart3,
  AppWindow,
  Settings,
  LogOut,
  Menu,
  Sun,
  Moon,
  X,
} from 'lucide-react';
import { useApi } from './auth/useApi';
import { setViewAs, PreviewRole } from './auth/viewAs';
import { Dashboard } from './pages/Dashboard';
import { TicketsList } from './pages/TicketsList';
import { TicketDetail } from './pages/TicketDetail';
import { NewTicket } from './pages/NewTicket';
import { AdminSettings } from './pages/AdminSettings';
import { KnowledgeBase } from './pages/KnowledgeBase';
import { Reports } from './pages/Reports';
import { Assets } from './pages/Assets';
import { Applications } from './pages/Applications';
import { Projects } from './pages/Projects';
import { Changelog } from './pages/Changelog';

// Forest green palette (matches the prototype)
const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  muted: 'var(--c-muted)',
  sidebar: 'var(--c-sidebar)',
};

export const APP_VERSION = 'v1.41.2';
// Treat anything < 900px as "narrow" — that's where we collapse the sidebar.
const MOBILE_BP = 900;

interface Me {
  userId: number;
  email: string;
  displayName: string;
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

const Wordmark: React.FC = () => (
  <div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <div style={{
        fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: 22,
        color: '#ffffff', letterSpacing: '-0.5px',
      }}>
        HelpDesk<span style={{ color: C.secondary }}>Pro</span>
      </div>
      <div style={{
        fontSize: 10, color: 'rgba(255,255,255,0.55)', fontWeight: 500,
        letterSpacing: '0.5px',
      }}>
        {APP_VERSION}
      </div>
    </div>
    <div style={{
      fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 2,
      letterSpacing: '0.4px',
    }}>
      Made by Society Brands IT Team
    </div>
  </div>
);

interface NavConfig {
  to: string;
  label: string;
  icon: React.ReactNode;
  roles: Me['role'][];
}

const NAV: NavConfig[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={18} />, roles: ['admin', 'tech', 'user', 'viewer', 'agent'] },
  { to: '/tickets',   label: 'Tickets',   icon: <TicketIcon size={18} />,      roles: ['admin', 'tech', 'user', 'viewer', 'agent'] },
  { to: '/kb',        label: 'Knowledge Base', icon: <BookOpen size={18} />,   roles: ['admin', 'tech', 'viewer', 'user', 'agent'] },
  { to: '/projects',  label: 'Projects',  icon: <FolderKanban size={18} />,    roles: ['admin', 'tech', 'viewer'] },
  { to: '/reports',   label: 'Reports',   icon: <BarChart3 size={18} />,       roles: ['admin', 'tech', 'viewer'] },
  { to: '/apps',      label: 'Applications', icon: <AppWindow size={18} />,    roles: ['admin', 'tech'] },
  { to: '/assets',    label: 'Hardware',  icon: <Laptop size={18} />,          roles: ['admin', 'tech', 'viewer'] },
  { to: '/admin',     label: 'Admin',     icon: <Settings size={18} />,        roles: ['admin'] },
  { to: '/admin',     label: 'Admin',  icon: <Settings size={18} />,        roles: ['tech'] },
  { to: '/changelog', label: 'Changelog', icon: <ScrollText size={18} />,      roles: ['admin', 'tech', 'agent'] },
];

/** Hook: returns whether the current viewport is "narrow" (mobile/tablet). */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < MOBILE_BP : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

/** Sidebar content — same on desktop and the mobile drawer. */
const SidebarContent: React.FC<{
  me: Me; items: NavConfig[]; onNavigate: () => void;
  realRole?: Me['role']; previewRole?: PreviewRole | null;
  onPreview?: (r: PreviewRole) => void; onExitPreview?: () => void;
}> = ({ me, items, onNavigate, realRole, previewRole, onPreview, onExitPreview }) => {
  const { instance } = useMsal();
  const [dark, setDark] = useState(
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
  );
  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    const root = document.documentElement;
    if (next) root.setAttribute('data-theme', 'dark');
    else root.removeAttribute('data-theme');
    try { localStorage.setItem('hdp-theme', next ? 'dark' : 'light'); } catch (e) { /* ignore */ }
  };
  return (
    <>
      <div style={{ padding: '0 20px 24px' }}>
        <Wordmark />
      </div>
      <nav style={{ flex: 1 }}>
        {items.map(n => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onNavigate}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 20px', color: '#ffffff', textDecoration: 'none', fontSize: 14,
              background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
              borderLeft: isActive ? `3px solid ${C.secondary}` : '3px solid transparent',
              minHeight: 44, // mobile tap target
            })}
          >
            {n.icon}
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        {realRole === 'admin' && onPreview && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>View as</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(['tech', 'agent', 'user', 'viewer'] as PreviewRole[]).map(r => (
                <button key={r} onClick={() => onPreview(r)} style={{
                  background: previewRole === r ? C.secondary : 'transparent',
                  color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                  padding: '5px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                  textTransform: 'capitalize',
                }}>{r}</button>
              ))}
              {previewRole && (
                <button onClick={() => onExitPreview && onExitPreview()} style={{
                  background: 'transparent', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.3)',
                  padding: '5px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                }}>Exit</button>
              )}
            </div>
          </div>
        )}
        <div style={{ fontSize: 13, fontWeight: 600 }}>{me.displayName}</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}>
          {me.role}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button onClick={toggleTheme} aria-label="Toggle dark mode" style={{
            background: 'transparent', color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.3)', padding: '8px 12px',
            borderRadius: 4, fontSize: 12, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 36,
          }}>
            {dark ? <Sun size={12} /> : <Moon size={12} />} {dark ? 'Light' : 'Dark'}
          </button>
          <button onClick={() => instance.logoutRedirect()} style={{
            background: 'transparent', color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.3)', padding: '8px 12px',
            borderRadius: 4, fontSize: 12, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 36,
          }}>
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </div>
    </>
  );
};

/** Mobile top bar — only visible when viewport is narrow. */
const MobileTopBar: React.FC<{ onMenuClick: () => void }> = ({ onMenuClick }) => (
  <div style={{
    position: 'sticky', top: 0, zIndex: 50,
    background: C.sidebar, color: '#fff',
    padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  }}>
    <button
      onClick={onMenuClick}
      style={{
        background: 'transparent', border: 'none', color: '#fff',
        padding: 8, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 4, minWidth: 40, minHeight: 40,
      }}
      aria-label="Open menu"
    >
      <Menu size={22} />
    </button>
    <Wordmark />
  </div>
);

const Shell: React.FC<{
  me: Me; children: React.ReactNode;
  realRole?: Me['role']; previewRole?: PreviewRole | null;
  onPreview?: (r: PreviewRole) => void; onExitPreview?: () => void;
}> = ({ me, children, realRole, previewRole, onPreview, onExitPreview }) => {
  const items = NAV.filter(n => n.roles.includes(me.role));
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const banner = previewRole ? (
    <div style={{
      background: C.secondary, color: '#fff', padding: '8px 16px',
      fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 12, flexWrap: 'wrap',
    }}>
      <span>Viewing as <strong style={{ textTransform: 'capitalize' }}>{previewRole}</strong> — this is a preview of what they see.</span>
      <button onClick={() => onExitPreview && onExitPreview()} style={{
        background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.5)',
        padding: '3px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
      }}>Exit preview</button>
    </div>
  ) : null;

  const sidebarProps = { realRole, previewRole, onPreview, onExitPreview };

  if (!isMobile) {
    // Desktop: side-by-side
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: C.bg }}>
        <aside style={{
          width: 220, background: C.sidebar, color: '#ffffff',
          padding: '20px 0', display: 'flex', flexDirection: 'column',
          flexShrink: 0,
        }}>
          <SidebarContent me={me} items={items} onNavigate={() => {}} {...sidebarProps} />
        </aside>
        <main style={{ flex: 1, overflow: 'auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {banner}
          <div style={{ padding: 32 }}>{children}</div>
        </main>
      </div>
    );
  }

  // Mobile: top bar + drawer overlay
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', flexDirection: 'column' }}>
      <MobileTopBar onMenuClick={() => setDrawerOpen(true)} />
      {banner}

      {/* Drawer overlay */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100,
          }}
        >
          <aside
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute', top: 0, left: 0, bottom: 0,
              width: '85%', maxWidth: 280, background: C.sidebar, color: '#fff',
              padding: '20px 0', display: 'flex', flexDirection: 'column',
              boxShadow: '4px 0 12px rgba(0,0,0,0.25)',
              animation: 'slideIn 0.18s ease-out',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 12px 4px' }}>
              <button
                onClick={() => setDrawerOpen(false)}
                style={{
                  background: 'transparent', border: 'none', color: '#fff',
                  padding: 6, cursor: 'pointer', borderRadius: 4,
                  minWidth: 36, minHeight: 36,
                }}
                aria-label="Close menu"
              ><X size={20} /></button>
            </div>
            <SidebarContent me={me} items={items} onNavigate={() => setDrawerOpen(false)} {...sidebarProps} />
          </aside>
        </div>
      )}

      <main style={{
        flex: 1, padding: 16, overflow: 'auto', minWidth: 0,
      }}>{children}</main>

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};


export const App: React.FC = () => {
  const api = useApi();
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewRole, setPreviewRole] = useState<PreviewRole | null>(null);

  useEffect(() => {
    api.get<Me>('/api/users/me')
      .then(setMe)
      .catch((e: Error) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreview = (r: PreviewRole) => { setViewAs(r); setPreviewRole(r); };
  const exitPreview = () => { setViewAs(null); setPreviewRole(null); };

  if (error) {
    return (
      <div style={{ padding: 32, color: '#b00020' }}>
        Failed to load your profile: {error}
      </div>
    );
  }
  if (!me) {
    return <div style={{ padding: 32, color: C.muted }}>Loading…</div>;
  }

  // Only admins can preview. The effective `me` drives all UI gating + data,
  // while the real role (me.role) controls who can see/use the switcher.
  const canPreview = me.role === 'admin';
  const effectiveMe: Me = canPreview && previewRole ? { ...me, role: previewRole } : me;

  return (
    <BrowserRouter>
      <Shell
        me={effectiveMe}
        realRole={me.role}
        previewRole={canPreview ? previewRole : null}
        onPreview={canPreview ? applyPreview : undefined}
        onExitPreview={exitPreview}
      >
        {/* Remount the routed subtree when the preview role changes so every
            page refetches its data under the new effective role. */}
        <div key={previewRole ?? 'real'}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard me={effectiveMe} />} />
            <Route path="/tickets"   element={<TicketsList me={effectiveMe} />} />
            <Route path="/tickets/new" element={<NewTicket me={effectiveMe} />} />
            <Route path="/tickets/:id" element={<TicketDetail me={effectiveMe} />} />
            <Route path="/kb"        element={<KnowledgeBase me={effectiveMe} />} />
            <Route path="/assets"    element={<Assets me={effectiveMe} />} />
            <Route path="/projects"  element={<Projects me={effectiveMe} />} />
            <Route path="/apps"      element={<Applications me={effectiveMe} />} />
            <Route path="/reports"   element={<Reports me={effectiveMe} />} />
            <Route path="/admin"     element={<AdminSettings me={effectiveMe} />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="*"          element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </Shell>
    </BrowserRouter>
  );
};
